/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { diff, applyChange, revertChange } from 'deep-diff'
import type { PreFilter, Diff } from 'deep-diff'
import { cloneDeep } from 'lodash-es'
import { HistoryRecorder } from '@com/utils/history-recorder'

interface Fn<T> {
  (...args: any[]): T
}

type State<T> = Fn<T> | T

export type TracksDiff<T> = Diff<{ value: T }>[]

export interface RecorderOptions<T> {
  prefilter?: PreFilter<T>
  max?: number
}
export class Recorder<T> extends HistoryRecorder<TracksDiff<T>> {
  savedState: T
  options: RecorderOptions<T>

  constructor(private state: State<T>, options: RecorderOptions<T>) {
    super(options.max)
    this.options = options
    this.savedState = this.save()
  }

  save() {
    this.savedState = cloneDeep(this._getState())
    return this.savedState
  }

  _getState(): T {
    const state = this.state as any
    return typeof state === 'function' ? state() : state
  }

  push() {
    const value = this._getState()
    const change = diff(
      { value: this.savedState },
      { value: cloneDeep(value) },
      this.options.prefilter as any,
    )

    if (change) {
      this.savedState = cloneDeep(value)
      super.push(change)
    }

    return change
  }

  undo() {
    const state = this._getState()
    const change = super.undo()
    if (change) {
      const cloned = cloneDeep(state)
      change.forEach((item) => {
        revertChange({ value: cloned }, { value: this.savedState }, item)
      })

      this.savedState = cloned
    }

    return change
  }

  redo() {
    const state = this._getState()
    const change = super.redo()
    if (change) {
      const cloned = cloneDeep(state)
      change.forEach((item) => {
        applyChange({ value: cloned }, { value: this.savedState }, item)
      })

      this.savedState = cloned
    }

    return change
  }
}
